import React, { useState, useEffect } from 'react';
import { useStripe, useElements, CardElement} from '@stripe/react-stripe-js';
import { DateTime } from 'luxon';
import NumberFormat from 'react-number-format';

import { useTheme, makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import Collapse from '@material-ui/core/Collapse';
import Divider from '@material-ui/core/Divider';
import Alert from '@material-ui/lab/Alert';
import InputAdornment from '@material-ui/core/InputAdornment';

import States from 'app/utils/States';
import Months from 'app/utils/Months';

import useApi from 'app/context/useApi';
import useTerminal from 'app/context/useTerminal';

const AmountInput = (props) => (
    <TextField
        id="donation-amount"
        size="small"
        variant="outlined"
        fullWidth
        inputProps={{
            style: {
                textAlign: 'right'
            }
        }}
        {...props}
        InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
    />
)

const useStyles = makeStyles(theme => ({
    elementBase: {
        fontSize: theme.typography.body1.fontSize,
        fontWeight: theme.typography.body1.fontWeight,
        fontFamily: theme.typography.body1.fontFamily,
        lineHeight: '1.1876em',
        letterSpacing: theme.typography.body1.letterSpacing,
        color: theme.palette.text.primary,
        color: 'rgba(0, 0, 0, 0.87)',
        cursor: 'text',
        padding: '18.5px 14px',
        borderColor: 'rgba(0, 0, 0, 0.23)',
        borderRadius: theme.shape.borderRadius,
        borderWidth: 2,
        borderStyle: 'solid',
        "&:hover": {
            borderColor: 'rgba(0, 0, 0, 0.87)'
        },
    },
    elementComplete: {
        borderColor: theme.palette.success.main + ' !important',
        borderWidth: 2,
    },
    elementEmpty: {

    },
    elementFocus: {
        borderColor: theme.palette.primary.main + ' !important',
        borderWidth: 2,
    },
    elementInvalid: {
        borderColor: theme.palette.error.main + ' !important',
        borderWidth: 2,
    },
    dialogTotal: {
        marginTop: theme.spacing(3)
    },
    total: {
        marginTop: theme.spacing(1)
    },
    alert: {
        marginTop: theme.spacing(2)
    },
    notActive: {
        paddingTop: theme.spacing(6),
        paddingBottom: theme.spacing(6),
        textAlign: 'center'
    },
    paymentMethodSelectContainer: {
        marginBottom: theme.spacing(3)
    },
    payButton: {
        width: '100%',
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    },
    divider: {
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1)
    },
    alignRight: {
        textAlign: 'right'
    }
}));

const initValues = {
    name: "",
    email: "",
    address_line1: "",
    address_line2: "",
    address_city: "",
    address_state: "",
    address_zip: "",
    address_country: 'US',
}

export default function ChargePaymentMethodForm(props) {

    const { invoice, user, club, urlPrefix, onCharge, onClose, loading, setLoading , canUseTerminal} = props;

    const classes = useStyles();
    const stripe = useStripe();
    const elements = useElements();
    const theme = useTheme();
    const api = useApi();
    const { terminal } = useTerminal();

    const [ values, setValues ] = useState(initValues);
    const [ items, setItems ] = useState([]);
    const [ clientSecret, setClientSecret ] = useState(null);
    const [ paymentId, setPaymentId ] = useState(null);
    const [ paymentIntentId, setPaymentIntentId ] = useState(null);
    const [ paymentMethod, setPaymentMethod ] = useState('new');
    const [ defaultMethod, setDefaultMethod ] = useState(null);
    const [ paymentMethods, setPaymentMethods ] = useState([]);
    const [ donationAmount, setDonationAmount ] = useState(0.00);
    const [ subTotal, setSubTotal ] = useState(0.00);
    const [ convenienceFee, setConveniencefee ] = useState(0.00);
    const [ total, setTotal ] = useState(0.00);

    const [ errors, setErrors ] = useState([]);
    const [ paymentMethodsLoading, setPaymentMethodsLoading ] = useState(false);
    const [ stripeLoaded, setStripeLoaded ] = useState(false);
    const [ closing, setClosing ] = useState(false);

    const getPaymentMethods = () => {

        setPaymentMethodsLoading(true);

        api.get(urlPrefix + '/payment/method')
            .then(res => {
                setPaymentMethods(res.methods);
                setDefaultMethod(res.default_payment_method);
            }).finally(() => setPaymentMethodsLoading(false));
    }

    const getIntent = () => {

        setLoading(true);

        const intentData = {
            invoice_id: invoice.id,
            user_id: user.id,
            club_id: club.id
        }

        api.post('/stripe/intent', intentData)
            .then(res => {
                setClientSecret(res.client_secret);
                setPaymentIntentId(res.payment_intent_id);
            })
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).finally(() => setLoading(false));
    }

    const handleUser = () => {
        setValues({
            name: user.first + " " + user.last,
            email: user.email || "",
            address_line1: user.address_one || "",
            address_line2: user.address_two || "",
            address_city: user.city || "",
            address_state: user.state || "",
            address_zip: user.zip || "",
            address_country: user.country || "US"
        })
    };

    useEffect(() => {
        if (invoice && user && !closing) {
            getIntent();
        }
        if (invoice && invoice.invoice_items) {
            setItems(invoice.invoice_items);
        }
    }, [invoice, user]);

    useEffect(() => {
        if (user) {
            handleUser();
            getPaymentMethods();
        }
    }, [user])

    useEffect(() => {
        setStripeLoaded((stripe && elements) ? true : false);
    }, [stripe, elements]);

    useEffect(() => {
        if (paymentMethods.length > 0) {
            const dpm = paymentMethods.find(method => method.id === defaultMethod);
            if (dpm) {
                setPaymentMethod(dpm.id);
            } else {
                setPaymentMethod(paymentMethods[0].id)
            }
        }
    }, [paymentMethods, defaultMethod]);

    // Getters ---------------------------------------------

    const handlePaymentMethod = event => {
        if (event.target.value === 'manage') {
            onClose('/profile/payment');
        }
        setPaymentMethod(event.target.value);
    }

    useEffect(() => {
        if (paymentIntentId) {
            setLoading(true);

            const data = {
                invoice_id: invoice.id,
                user_id: user.id,
                payment_method: paymentMethod,
                payment_intent_id: paymentIntentId
            }

            api.post('/stripe/payment/update', data)
                .then(() => {
                    if (paymentMethod === 'terminal') prepareTerminal();
                })
                .finally(() => setLoading(false));
        }

    }, [paymentMethod]);

    const handleChange = name => event => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        if (isError(name)) delete errors[name];

        setValues({ ...values, [name]: value });
    };

    const handleDonationAmount = (values) => {

        const value = values.floatValue;

        if (value == undefined) {
            setDonationAmount(0);
        } else {
            setDonationAmount(value);
        }
    }

    const prepareTerminal = async () => {     

        const result = await terminal.collectPaymentMethod(clientSecret);

        if (result.error) {
            setErrors({stripe: result.error.message});
            setLoading(false);
        } else {
            processTerminalPayment(result.paymentIntent);
        }
    }

    const processTerminalPayment = async (terminalPaymentIntent) => {

        setLoading(true);

        let currentPaymentId = paymentId;
        
        if (! currentPaymentId) {
            const createPaymentData = {
                invoice_id: invoice.id,
                user_id: user.id,
                payment_method: 'terminal',
                donation_amount: donationAmount,
                payment_intent_id: paymentIntentId
            }

            const createPaymentResponse = await api.post('/stripe/payment/create', createPaymentData);

            setLoading(false);

            currentPaymentId = createPaymentResponse.payment_id;
            setPaymentId(createPaymentResponse.payment_id);
        }

        const result = await terminal.processPayment(terminalPaymentIntent);

        if (result.error) {

            if (result.error.status === 'requires_confirmation') {
                processTerminalPayment(terminalPaymentIntent);
                setErrors({stripe: result.error.message});
            } else if (result.error.status === 'requires_payment_method') {
                prepareTerminal();
                setErrors({stripe: result.error.message});
            } else {
                setErrors({stripe: result.error.message});
                setLoading(false);

                api.post('/stripe/payment/' + currentPaymentId + '/error', result.error).finally(() => {
                    setLoading(false)
                    setErrors({ stripe: result.error.message });
                });
            }
        } else if (result.paymentIntent) {
            // Placeholder for notifying your backend to capture result.paymentIntent.id
            const UpdatePaymentData = {stripe_intent: result.paymentIntent};
            postPaymentSucceeded(UpdatePaymentData, currentPaymentId);
        }
    }

    const postCharge = async (event) => {

        event.preventDefault();

        setLoading(true);
        setErrors({});

        if (! stripeLoaded) {
            setErrors({stripe: 'Unable to connect to our payment processor. Please try again later.'});
            setLoading(false);
            return false;
        }

        // Let's first validate fields on our server so we don't show errors on a successful charge.
        // Creates a payment that we can update throughout this process
        let currentPaymentId = paymentId;

        let currentPaymentMethod = (paymentMethod !== 'new') ? paymentMethod : {
            card: elements.getElement(CardElement),
            billing_details: {
                name: values.name,
                email: values.email,
                address: {
                    line1: values.address_one,
                    line2: values.address_two,
                    city: values.city,
                    state: values.state,
                    postal_code: values.zip
                }
            },
        }

        if (! currentPaymentId) {
            const createPaymentData = {
                invoice_id: invoice.id,
                user_id: user.id,
                payment_method: (paymentMethod !== 'new') ? currentPaymentMethod : 'new',
                donation_amount: donationAmount,
                payment_intent_id: paymentIntentId
            }

            const createPaymentResponse = await api.post('/stripe/payment/create', createPaymentData)

            setPaymentId(createPaymentResponse.payment_id);

            currentPaymentId = createPaymentResponse.payment_id;

            if (createPaymentResponse.payment_method_id) {
                currentPaymentMethod = createPaymentResponse.payment_method_id;
            }
        }

        const confirmPaymentData = { payment_method: currentPaymentMethod };

        //if (savePaymentInformation) confirmCardData.setup_future_usage = 'off_session';

        // Then we are going to submit the card payment to stripe

        let result = null;

        if (paymentMethod === 'new') {
            result = await stripe.confirmCardPayment(clientSecret, confirmPaymentData);
        } else {
            const type = paymentMethods.find(method => method.id === paymentMethod).type;

            switch (type) {
                case 'card':
                    result = await stripe.confirmCardPayment(clientSecret, confirmPaymentData);
                    break;
                case 'us_bank_account':
                    result = await stripe.confirmUsBankAccountPayment(clientSecret, confirmPaymentData);
                    break;
            }
        }

        // If Error, let's display the error and update the payment on our servers
        if (result.error) {
            api.post('/stripe/payment/' + currentPaymentId + '/error', result.error).finally(() => {
                setLoading(false)
                setErrors({ stripe: result.error.message });
            });
        } else {
            const UpdatePaymentData = {stripe_intent: result.paymentIntent};
            postPaymentSucceeded(UpdatePaymentData, currentPaymentId);
        }
    }

    const postPaymentSucceeded = (paymentData, currentPaymentId) => {

        setLoading(true);

        api.post('/stripe/payment/' + currentPaymentId + '/succeeded', paymentData)
            .then(() => {
                setClosing(true);
                onCharge();
            })
            .catch(error => {
                setLoading(false)

                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            });
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const handleCardError = event => {
        if (event.error) {
            setErrors({...errors, stripe: event.error.message })
        } else {
            setErrors({...errors, stripe: undefined });
        }
    }

    useEffect(() => {

        let pm = {type: 'new'};

        switch (paymentMethod) {
            case 'new':
            case 'terminal':
            case 'manage':
                pm = {type: 'new'};
                break;
            default:
                pm = paymentMethods.find(method => method.id === paymentMethod);
        }

        const itemTotal = items.reduce((total, item) => {
            return total + (item.quantity * item.amount)
        }, 0);

        const sub = (itemTotal + donationAmount);

        const staticFee = 0.30;
        const dynamicFee = 3.9; // 2.9 stripe and 1 CA
        const achFee = 0.8; // .8 stripe and 1 CA

        let amountDue = (sub - invoice?.amount_paid);
        let convFee = 0;

        if (invoice?.include_convenience_fees) {
            switch (pm.type) {
                case 'new':
                case 'card':
                    amountDue = ((amountDue + staticFee) / (1 - (dynamicFee / 100)));
                    convFee = ((amountDue * (dynamicFee / 100)) + staticFee);
                    break;
                case 'us_bank_account':
                    amountDue = (amountDue / (1 - (achFee / 100)));
                    convFee = ((amountDue * (achFee / 100)));
                    break;
            }
        }

        setSubTotal(sub);
        setTotal(amountDue);
        setConveniencefee(convFee);

    }, [items, paymentMethod, donationAmount])

    return (
        <form>
            <div className={classes.paymentMethodSelectContainer}>
                <TextField
                    id="payment-method-select"
                    select
                    value={paymentMethod}
                    onChange={handlePaymentMethod}
                    variant="outlined"
                    fullWidth
                    >
                    {paymentMethods.map((method) => {
                        switch (method.type) {
                            case 'card': return (
                                <MenuItem key={method.id} value={method.id}>
                                    <Grid container justify="space-between" alignItems="center" spacing={1}>
                                        <Grid item>
                                            <Typography variant="subtitle1" display="inline">
                                                {window.ucFirst(method.card.brand)}&nbsp;&bull;&bull;&bull;&bull;&nbsp;{method.card.last4}
                                            </Typography>
                                            <Typography variant="body1" display="inline" color="primary">
                                                {(method.id === defaultMethod) ? '  Default' : null}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            {(DateTime.fromObject({
                                                year: method.card.exp_year,
                                                month: method.card.exp_month
                                            }) > DateTime.local()) ? (
                                                <Typography variant="subtitle2" color="textSecondary">
                                                    {`Expires ${Months[method.card.exp_month].full} ${method.card.exp_year}`}
                                                </Typography>
                                            ) : (
                                                <Typography variant="subtitle2" color="error">
                                                    {`Expired ${Months[method.card.exp_month].full} ${method.card.exp_year}`}
                                                </Typography>
                                            )}
                                        </Grid>
                                    </Grid>
                                </MenuItem>
                            )
                            case 'us_bank_account': return (
                                <MenuItem key={method.id} value={method.id}>
                                    <Grid container justify="space-between" alignItems="center" spacing={1}>
                                        <Grid item>
                                            <Typography variant="subtitle1" display="inline">
                                                {window.ucFirst(method.us_bank_account.bank_name)}&nbsp;&bull;&bull;&bull;&bull;&nbsp;{method.us_bank_account.last4}
                                            </Typography>
                                            <Typography variant="body1" display="inline" color="primary">
                                                {(method.id === defaultMethod) ? '  Default' : null}
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography variant="subtitle2" color="textSecondary">
                                                USD
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </MenuItem>
                            )
                        }

                    })}
                    <MenuItem value='new'>
                        Charge a new card
                    </MenuItem>
                    {(canUseTerminal && terminal) &&
                        <MenuItem value='terminal'>
                            Collect payment via Terminal
                        </MenuItem>
                    }
                    <Divider className={classes.divider}/>
                    {onClose &&
                        <MenuItem value="manage">
                            Manage your saved Payment Methods
                        </MenuItem>
                    }
                </TextField>
            </div>
            <Collapse in={(paymentMethod === 'new' && ! paymentMethodsLoading && stripeLoaded)}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            id="input-name"
                            label="Cardholder Name"
                            value={values.name}
                            onChange={handleChange('name')}
                            fullWidth
                            error={isError('name')}
                            helperText={getErrorMessage('name')}
                            variant="outlined"
                            autoFocus
                            />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            id="input-email"
                            label="Cardholder Email"
                            value={values.email}
                            onChange={handleChange('email')}
                            fullWidth
                            error={isError('email')}
                            helperText={getErrorMessage('email')}
                            variant="outlined"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="input-address-one"
                            label="Address One"
                            value={values.address_line1}
                            onChange={handleChange('address_line1')}
                            fullWidth
                            error={isError('address_line1')}
                            helperText={getErrorMessage('address_line1')}
                            variant="outlined"
                            />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="input-address-two"
                            label="Address Two"
                            value={values.address_line2}
                            onChange={handleChange('address_line2')}
                            fullWidth
                            error={isError('address_line2')}
                            helperText={getErrorMessage('address_line2')}
                            variant="outlined"
                            />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            id="input-city"
                            label="City"
                            fullWidth
                            value={values.address_city}
                            onChange={handleChange('address_city')}
                            error={isError('address_city')}
                            helperText={getErrorMessage('address_city')}
                            variant="outlined"
                            />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            id="input-state"
                            select
                            fullWidth
                            label="State"
                            value={values.address_state}
                            onChange={handleChange('address_state')}
                            error={isError('address_state')}
                            helperText={getErrorMessage('address_state')}
                            variant="outlined"
                            >
                            {States.map( state => {
                                return (
                                    <MenuItem key={state.abbreviation} value={state.abbreviation} >
                                        {state.name}
                                    </MenuItem>
                                )
                            })}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <TextField
                            id="input-zip"
                            label="Zip"
                            fullWidth
                            value={values.address_zip}
                            onChange={handleChange('address_zip')}
                            error={isError('address_zip')}
                            helperText={getErrorMessage('address_zip')}
                            variant="outlined"
                            />
                    </Grid>
                    <Grid item xs={12}>
                        <CardElement
                            onChange={handleCardError}
                            options={{
                                hidePostalCode: true,
                                classes: {
                                    base: classes.elementBase,
                                    complete: classes.elementComplete,
                                    empty: classes.elementEmpty,
                                    focus: classes.elementFocus,
                                    invalid: classes.elementInvalid
                                },
                                style: {
                                    base: {
                                        iconColor: theme.palette.primary.dark,
                                    },
                                },
                            }}
                        />
                    </Grid>
                </Grid>
            </Collapse>
            <div className={classes.dialogTotal}>
                {items.map((item) => (
                    <Grid
                        container
                        spacing={1}
                        justify="space-between"
                        alignItems="center"
                        key={item.id}
                        >
                        <Grid item>
                            <Typography variant="subtitle2" color="textSecondary" display="inline">
                                {item.name}
                            </Typography>
                            &nbsp;&nbsp;&nbsp;
                            <Typography variant="caption" color="textSecondary" display="inline">
                                {item.quantity} x ${item.amount_f}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <div className="tr">
                                <Typography display="inline" variant="subtitle2" color="textSecondary">
                                    ${item.total_f}
                                </Typography>
                            </div>
                        </Grid>
                    </Grid>
                ))}
                {club.meta.display_donation_field &&
                    <Grid
                        container
                        spacing={1}
                        justify="space-between"
                        alignItems="center"
                        >
                        <Grid item xs={9}>
                            <Typography
                                align="right"
                                variant="subtitle2"
                                >
                                Add a donation:
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <NumberFormat
                                id="input-donation-amount"
                                thousandSeparator
                                decimalScale={2}
                                fixedDecimalScale
                                allowNegative={false}
                                allowEmptyFormatting={true}
                                customInput={AmountInput}
                                defaultValue={0.00}
                                value={donationAmount}
                                onValueChange={handleDonationAmount}
                                error={isError('donation_amount')}
                                disabled={paymentId !== null}
                            />
                        </Grid>
                    </Grid>
                }
                {invoice &&
                    <div className={classes.total}>
                        <Grid container spacing={1} justify="space-between" alignItems="center">
                            <Grid item xs={9}>
                                <Typography
                                    align="right"
                                    variant="subtitle2"
                                    color="primary"
                                    >
                                    SubTotal:
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography align="right" variant="subtitle2" color="textPrimary">
                                    ${subTotal.toFixed(2)}
                                </Typography>
                            </Grid>
                        </Grid>

                        {(invoice.amount_paid > 0) &&
                            <Grid container spacing={1} justify="space-between" alignItems="center">
                                <Grid item xs={9}>
                                    <Typography
                                        align="right"
                                        variant="subtitle2"
                                        color="textSecondary"
                                        >
                                        Previously Paid:
                                    </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography
                                        align="right"
                                        variant="subtitle2"
                                        color="textSecondary"
                                        >
                                        -(${invoice.amount_paid_f})
                                    </Typography>
                                </Grid>
                            </Grid>
                        }
                        {invoice.include_convenience_fees &&
                            <Grid container spacing={1} justify="space-between" alignItems="center">
                                <Grid item xs={9}>
                                    <Typography
                                        align="right"
                                        variant="subtitle2"
                                        color="textSecondary"
                                        >
                                        Convenience Fee:
                                    </Typography>
                                </Grid>
                                <Grid item xs={3}>
                                    <Typography
                                        align="right"
                                        variant="subtitle2"
                                        color="textSecondary"
                                        >
                                        ${convenienceFee.toFixed(2)}
                                    </Typography>
                                </Grid>
                            </Grid>
                        }
                        <Grid container spacing={1} justify="space-between" alignItems="center">
                            <Grid item xs={9}>
                                <Typography
                                    align="right"
                                    variant="subtitle2"
                                    color="primary"
                                    >
                                    Total:
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography
                                    align="right"
                                    variant="subtitle2"
                                    color="textPrimary"
                                    >
                                    ${total.toFixed(2)}
                                </Typography>
                            </Grid>
                        </Grid>
                    </div>
                }
            </div>
            {isError('stripe') &&
                <Alert variant="filled" severity="error" className={classes.alert}>
                    {getErrorMessage('stripe')}
                </Alert>
            }
            <Button
                onClick={postCharge}
                color="primary"
                disabled={!stripeLoaded || loading || !user || !invoice || paymentMethod === 'terminal'}
                variant="contained"
                className={classes.payButton}
                >
                Pay
            </Button>
        </form>
    )
}
